import type { Template } from "@/mock";
import { cn } from "@/lib/cn";

/**
 * Data-driven gradient thumbnail for a template. The two stops come straight
 * from the fixture (hex), so hardcoded color here is sanctioned by the brief.
 * A faint diagonal sheen + wordmark ghost gives it gallery weight without art.
 */
export function TemplateThumb({
  thumb,
  label,
  className,
  ratio = "aspect-[4/3]",
}: {
  thumb: Template["thumb"];
  label?: string;
  className?: string;
  ratio?: string;
}) {
  return (
    <div
      className={cn("relative overflow-hidden", ratio, className)}
      style={{ background: `linear-gradient(135deg, ${thumb[0]}, ${thumb[1]})` }}
    >
      {/* soft top sheen — atmosphere, not a glass card */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 80% at 20% 0%, rgba(255,255,255,0.22), transparent 55%)",
        }}
      />
      {/* ghosted page silhouette so each tile reads as a layout, not a swatch */}
      <div className="absolute inset-0 grid place-items-center p-5">
        <div className="w-full max-w-[78%] space-y-1.5 opacity-90">
          <div className="h-1.5 w-1/3 rounded-full bg-white/55" />
          <div className="h-3 w-3/4 rounded bg-white/85" />
          <div className="h-1.5 w-2/3 rounded-full bg-white/45" />
          <div className="mt-2 h-4 w-20 rounded bg-white/80" />
        </div>
      </div>
      {label && (
        <span className="absolute bottom-2 left-2.5 font-mono text-[10px] uppercase tracking-wide text-white/70">
          {label}
        </span>
      )}
    </div>
  );
}
